import type { CodeResponse } from '@react-oauth/google';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@/components';
import { RadixDialog } from '@/components/RadixDialog';
import { ConnectEmailMethod, CookieKey, ErrorMessages, MessageField, SignUpSource } from '@/constants';
import type { AuthenticateEMSPayload } from '@/fetchers/umsAPI/umsAuthenticator.fetchers';
import { useAuth, useGeo, useGlobalState } from '@/hooks';
import { ButtonSize } from '@/interfaces';
import { EmailSyncGoogleApiScopes } from '@/modules/pmt/constants';
import { useEmailSyncContext, useMyParcelsContext } from '@/modules/pmt/context';
import { gtmConnectEmailSuccess, gtmViewGrantAccessPopup } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import { GoogleOAuthFlow, useGoogleOAuth } from '../GoogleLoginButton/hooks';

const DialogAction = styled(Button).attrs({ isPMTButton: true, size: ButtonSize.Large })(({ theme }) => ({
  marginTop: theme.spacing[24],
  width: '100%',
}));

const InboxAccessDialog = () => {
  const { geoInfo } = useGeo();
  const { userInfo } = useGlobalState();
  const { trackingTranslation } = useMyParcelsContext();
  const { setSyncingAccount, isEmailSyncAllowed, fetchEmailSyncAccounts } = useEmailSyncContext();
  const { authEMS } = useAuth();
  const { logEvent } = useStatsigClient();

  const [showGrantedAccessDialog, setShowGrantedAccessDialog] = useState(false);

  const handleOAuthSuccess = async (response: CodeResponse) => {
    const { code, scope } = response;

    const systemLoginPayload: AuthenticateEMSPayload = {
      scope,
      code,
      signUpSource: SignUpSource.ConsumerWeb,
      newsletterOptIn: !!userInfo?.newsletterOptIn,
      country: userInfo?.country ?? geoInfo.country,
      redirectUri: 'postmessage',
    };
    try {
      const res = await authEMS(systemLoginPayload);

      if (!res.access_token || !res.user_info) {
        console.error(ErrorMessages[MessageField.EMAIL_SYNC_CANNOT_AUTHORIZED].error);
        return;
      }

      fetchEmailSyncAccounts();

      if (res.user_info.email_sync_opt_in) {
        // To show "Syncing in progress banner"
        // Ref: https://www.pivotaltracker.com/story/show/187111995
        setSyncingAccount(code);

        // https://www.pivotaltracker.com/story/show/187112079
        gtmConnectEmailSuccess(ConnectEmailMethod.GrantAccessPopup);
      }

      setShowGrantedAccessDialog(false);
    } catch (e) {
      console.error(e);
    }
  };

  const { login: retriggerGoogleOAuth } = useGoogleOAuth({
    onSuccess: handleOAuthSuccess,
    onError: (error) => console.error({ error }),
    flow: GoogleOAuthFlow.AuthCode,
    redirect_uri: 'postmessage',
    scope: EmailSyncGoogleApiScopes,
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const hasGrantedAllAccess = Cookies.get(CookieKey.HasGrantedAllAccess);
    if (isEmailSyncAllowed && hasGrantedAllAccess && !JSON.parse(hasGrantedAllAccess)) {
      timer = setTimeout(() => {
        setShowGrantedAccessDialog(true);
        Cookies.remove(CookieKey.HasGrantedAllAccess);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [isEmailSyncAllowed]);

  const closeDialog = () => setShowGrantedAccessDialog(false);

  return (
    <RadixDialog.WithGraphicWrapper open={showGrantedAccessDialog} onOpenChange={closeDialog}>
      <RadixDialog.Header closeable onClose={closeDialog} />
      <RadixDialog.Content>
        <RadixDialog.MainText>{trackingTranslation.mainTextGrantAccessPopup}</RadixDialog.MainText>
        <RadixDialog.SubText>{trackingTranslation.subTextGrantAccessPopup}</RadixDialog.SubText>
        <RadixDialog.Close asChild>
          <DialogAction
            onClick={() => {
              retriggerGoogleOAuth();
              gtmViewGrantAccessPopup({ variant: trackingTranslation.identifier });
              logEvent('view_grant_access_popup', undefined, { variant: trackingTranslation.identifier ?? '' });
            }}
          >
            {trackingTranslation.mainButtonGrantAccessPopup}
          </DialogAction>
        </RadixDialog.Close>
      </RadixDialog.Content>
    </RadixDialog.WithGraphicWrapper>
  );
};

export { InboxAccessDialog };
